import { UserGroupAssociatedArea } from "../user-groups/UserGroup";
import {StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {ViewSettingsState} from "../../reducers/common/ViewSettingsReducer";
import {
    changeColumnVisibility,
    handleSearchChange,
    fetchUserViewSettings,
    setListViewColumns,
    handleSortChange,
    handleRowsPerPageChange,
    handlePageChange,
    addDataFilter,
    removeDataFilter,
    changeViewTitle,
    sendUserToPage,
    initUserViewSettings,
    changeUserViewSettings, createUserViewSettings
} from "../../actions/common/UserViewSettingsActions";
import {Identifier} from "./Identifier";
import {LanguageInjectedProp} from "../../config/LanguageConfig";
import {AuthenticatedUserInjectedProp, UserId} from "../users/AuthenticatedUser";
import {ActiveDataFilter, DATA_FILTER_PREFIX, DataFilterTypes} from "./DataFilter";
import {ChangeHelpContextProp} from "../../actions/common/HelpActions";
import {TitleDisplayable} from "./ComponentBehavioural";


/**
 * A simple type alias representing a unique identifier for a view.
 *
 * This type is used to uniquely identify views within the application,
 * ensuring clarity and consistency when referencing or managing views.
 *
 * @remarks
 * - Typically, `ViewIdentifier` is used in contexts where unique view references are required, such as navigation or UI state management.
 * - It is expected to be a string, which can represent human-readable labels, UUIDs, or other unique string formats, depending on the use case.
 */
export type ViewIdentifier = string

/**
 * Represents an entity that can be uniquely identified by a view identifier.
 *
 * This interface ensures that any implementing object has a `viewIdentifier`
 * property, which can be used to distinguish it within a collection of views
 * or relate it to a specific view context. This is useful for tracking,
 * referencing, or associating interface elements in applications.
 */
export interface ViewIdentifiable {
    viewIdentifier: string
}

/**
 * Represents a dependency injected property for changing the view title.
 *
 * This interface is intended to standardize the mechanism for updating the title of a view within the application.
 * By injecting this property, components can decouple their logic from direct title change implementations,
 * promoting better modularity and testability.
 *
 * @property changeViewTitle A function reference to `changeViewTitle`, enabling title updates.
 */
export interface ChangeViewTitleInjectedProp {
    changeViewTitle: typeof changeViewTitle
}

/**
 * Represents the properties and methods injected into components that need to manage and interact with user view settings,
 * providing support for handling UI state, user preferences, and interactions in a list or data view.
 *
 * This interface is designed to enable dynamic, user-centric data views by offering utilities for managing behaviors like
 * sorting, pagination, search filters, and column visibility, while adhering to application-level user preferences.
 */
export interface GeneralViewInjectedProps extends LanguageInjectedProp,
    AuthenticatedUserInjectedProp,
    ChangeHelpContextProp,
    ChangeViewTitleInjectedProp
{
    fetchUserViewSettings: typeof fetchUserViewSettings
    handleSortChange: typeof handleSortChange
    handleSearchChange: typeof handleSearchChange
    handleRowsPerPageChange: typeof handleRowsPerPageChange
    handlePageChange: typeof handlePageChange,
    setListViewColumns: typeof setListViewColumns
    viewSettings: ViewSettingsState
    changeColumnVisibility: typeof changeColumnVisibility
    addDataFilter: typeof addDataFilter
    removeDataFilter: typeof removeDataFilter
    sendUserToPage: typeof sendUserToPage
    initUserViewSettings: typeof initUserViewSettings
    changeUserViewSettings: typeof changeUserViewSettings
    createUserViewSettings: typeof createUserViewSettings
}

/**
 * Maps the global application state to the properties required for a general view component.
 *
 * This function's business purpose is to connect specific pieces of the application state
 * (user authentication, view settings, and language preferences) to the props needed by
 * a general view component. This ensures seamless rendering of views customized for the
 * authenticated user with the appropriate settings and language.
 *
 * Internally, state slices maintained by individual reducers are extracted and
 * bundled into an object for consumption in containers or higher-level components.
 *
 * @param state The global application state managed by the Redux store.
 * @return An object containing three properties:
 * - `authUser` representing the authenticated user details, derived from the `AuthenticatedUserReducer`.
 * - `viewSettings` providing the settings/preferences for the view, derived from the `ViewSettingsReducer`.
 * - `language` conveying the current language or localization information, derived from the `LanguageReducer`.
 */
export const generalViewMapStateToProps = ( state: AppState ) => ({
    authUser: state.AuthenticatedUserReducer,
    viewSettings: state.ViewSettingsReducer,
    language: state.LanguageReducer
});

/**
 * Maps dispatchable actions to handlers for managing the state and behavior
 * of the general view in a user interface. This object enables seamless
 * interaction with user-specific settings, filters, and navigation workflows.
 *
 * The associated actions help ensure the view adapts dynamically to user
 * preferences, allowing customization and seamless data handling. It supports
 * pagination, sorting, column visibility, and more.
 *
 * @property fetchUserViewSettings - Fetches the stored settings for the user's view, such as layout or preferences.
 *                                   Typically called during initialization or page load.
 * @property handleSortChange - Updates the sorting criteria for displayed data. Ensures consistent order based on
 *                              the user's preferences or input.
 * @property handleSearchChange - Processes changes to the search query, altering the filtering of
 *                                displayed results accordingly.
 * @property handleRowsPerPageChange - Adjusts the number of rows displayed per page in the view. Updates the display
 *                                      and state to reflect this preference.
 * @property handlePageChange - Updates the current active page when interacting with pagination controls. Ensures the
 *                              appropriate data subset is displayed.
 * @property changeColumnVisibility - Toggles the visibility of specific columns in the list view. Allows end-users
 *                                     to customize the data display per their needs.
 * @property setListViewColumns - Configures or updates the set of displayed columns in the list view. Supports
 *                                 defining custom column arrangements.
 * @property addDataFilter - Applies a new data filter to refine displayed results. Enables granular searches and targeted insights.
 * @property removeDataFilter - Removes an existing filter, expanding the displayed results or undoing specific criteria.
 * @property sendUserToPage - Handles navigation to a specific page in the application. Useful for workflows requiring
 *                            programmatic redirection.
 * @property initUserViewSettings - Responsible for initializing default user view settings. Typically called during
 *                                   initial setup or when no stored settings exist.
 * @property changeUserViewSettings - Updates user-specific view settings dynamically. Can manage multiple preferences
 *                                     simultaneously.
 * @property createUserViewSettings - Creates and persists new view settings unique to the user. Called when a user
 *                                     customizes their preferences for the first time.
 */
export const generalViewDispatchToProps = {
    fetchUserViewSettings,
    handleSortChange,
    handleSearchChange,
    handleRowsPerPageChange,
    handlePageChange,
    changeColumnVisibility,
    setListViewColumns,
    addDataFilter,
    removeDataFilter,
    sendUserToPage,
    initUserViewSettings,
    changeUserViewSettings,
    createUserViewSettings
};

/**
 * Represents the unique identifier associated with a specific view in the system.
 *
 * This interface is used to encapsulate the identifier for a view, ensuring consistent
 * identification and retrieval of views across the application.
 *
 * @property viewId - A strongly-typed identifier uniquely associated with a view.
 */
export interface ViewId {
    viewId: Identifier<number>
}

/**
 * Represents a structured UI component associated with a user group and an organization.
 * This interface supports the management of views within an application, including properties for navigation, user interaction, and organizational association.
 *
 * The `View` interface is used to define metadata and configurations necessary for rendering and interacting with a specific view within the system.
 */
export interface View extends ViewId {
    area: UserGroupAssociatedArea
    parentViewId: number
    displayTitle: string
    linkTitle: Identifier<string>
    menuPosition: number
    isMenuItem: boolean
    isFavorite: boolean
    isSearchable: boolean
    isExportable: boolean
    companyId: number
}

/**
 * Represents a collection of user-specific view settings indexed by a unique key.
 *
 * This interface allows the application to manage and retrieve view configuration
 * settings for individual users or contexts. Each entry is mapped to a unique
 * identifier, which could represent a user ID, context name, or similar key.
 *
 * @property [Key: string] - A dynamic key where each key identifies a specific
 * set of user view settings. The value associated with each key is a `UserViewSettings`
 * object containing the configuration for that particular user or context.
 */
export interface UserViewSettingsList {
    [Key: string]: UserViewSettings
}

/**
 * Represents a collection of user-specific view settings, indexed by a unique identifier.
 *
 * This interface exists to organize and manage view configurations tailored to individual users.
 * It enables efficient retrieval and manipulation of these settings using their associated keys.
 *
 * Each key in the collection corresponds to a unique identifier (e.g., user ID), mapped to
 * a `UserViewSettings` object containing the user's specific settings for a view or interface.
 * The structure ensures flexibility for both small- and large-scale user bases.
 *
 * @template Key The unique identifier for a user's view settings.
 * @typedef UserViewSettings Represents the configuration details of a specific user's view settings.
 */
export interface ViewSettingsList {
    [Key: string]: UserViewSettings
}

/**
 * Represents configuration settings used to customize how data is viewed within the application.
 *
 * This interface extends `ViewIdentifiable` to inherit unique identification properties for specific views.
 * It includes properties to define the state of applied filters, enabling users to narrow down visible content
 * based on specific criteria.
 *
 * Constraints:
 * - `filters` can be null, representing a state where no filters are active.
 *
 * @extends ViewIdentifiable
 * @property filters Represents the active filters applied to the view. If null, no filters are applied.
 */
export interface ViewSettings extends ViewIdentifiable {
    filters: null
}

/**
 * get the initial state of the user view settings
 *
 * @param viewIdentifier
 * @param userId
 */
export const getInitialViewSettings = ( viewIdentifier: string, userId : number | UserId = 0 ): UserViewSettings => ({
    viewIdentifier: { rawValue: viewIdentifier },
    userId: typeof userId === 'number' ? { rawValue: userId } : userId,
    searchValue: null,
    activeFilters: [],
    numberOfEntriesPerPage: 40,
    selectedPageNumber: 1,
    orderByFields: [],
    columnState: [],
    userViewSettingsId: { rawValue: 0 },
    title: ''
});

/**
 * returns the current view settings for the specified view of the current user
 * if no settings were found, an initial view settings object will be returned
 *
 * @param viewIdentifier
 * @param store$
 */
export const getViewSettingsSafely = ( viewIdentifier: string, store$: StateObservable< AppState > ): UserViewSettings => {
    if( store$.value.ViewSettingsReducer.userViewSettings !== null && viewIdentifier in store$.value.ViewSettingsReducer.userViewSettings )
        return store$.value.ViewSettingsReducer.userViewSettings[ viewIdentifier ];
    else
        return getInitialViewSettings( viewIdentifier );
};

/**
 * Represents the unique identifier for a user's view settings.
 *
 * This interface is used to associate a specific user's view configurations
 * in the application with a unique identifier. It ensures that user preferences
 * can be referenced and updated systematically.
 *
 * @property userViewSettingsId - A strongly-typed identifier that uniquely
 * identifies the view settings for a user. This is tied to the user's specific
 * preferences.
 */
export interface UserViewSettingsId {
    userViewSettingsId: Identifier<number>
}

/**
 * Represents the customizable settings for a user's view of data or content.
 *
 * This interface defines the user's preferences and criteria for displaying
 * a collection of items, including pagination settings, applied filters,
 * sorting order, and the current state of columns. It is designed to persist
 * user-specific configurations for a consistent and personalized experience.
 */
export interface UserViewSettings extends UserViewSettingsId, TitleDisplayable {
    viewIdentifier: Identifier<string>
    userId: UserId
    searchValue: string | null
    activeFilters: ActiveDataFilter<any>[]
    numberOfEntriesPerPage: number
    selectedPageNumber: number
    orderByFields: OrderBy[]
    columnState: UserViewColumnState[]
}

/**
 * Represents the user view settings data passed to a component or module.
 *
 * Provides a structured property for accessing user-configurable view settings.
 * This is used to customize how information is displayed to the user in the interface.
 *
 * @property userViewSettings - An object containing the specific configuration for user view settings.
 */
export interface UserViewSettingsProp {
    userViewSettings: UserViewSettings
}

/**
 * Represents the state of a column in a user's view configuration.
 *
 * This interface is used to define the visibility and identification of a specific column
 * in a user-customizable table or grid view. It helps manage user preferences for which
 * columns are displayed or hidden in the interface.
 *
 * @property columnName - The unique name or identifier of the column.
 * @property isVisible - A boolean value indicating whether the column is visible (true)
 * or hidden (false).
 */
export interface UserViewColumnState {
    columnName: string
    isVisible: boolean
}

/**
 * Represents the types of date filters available for API operations.
 *
 * This type is used to standardize date-related filtering parameters when interacting with the API.
 * It ensures consistent usage of supported date types for querying or filtering data.
 *
 * - "START_DATE": Refers to the beginning date for a range or specific data filter criteria.
 * - "END_DATE": Refers to the ending date for a range or specific data filter criteria.
 * - "DATE": Represents a singular date without range context, typically used for exact date filtering.
 */
export type ApiFilterDateTypes = "START_DATE" | "END_DATE" | "DATE"

/**
 * Defines the allowable types for API filtering operations.
 *
 * This type is used to specify the accepted data types for filtering operations
 * within the API. This allows developers and stakeholders to define constraints
 * on how data can be queried and filtered, facilitating consistent implementation
 * of business requirements.
 *
 * The available filter types are:
 * - "ID": Used for numeric identifiers, such as database primary keys.
 * - "ID_STRING": Represents identifiers in string form, such as UUIDs.
 * - "BOOL": Represents a boolean (true/false) value.
 * - "STRING": Used for filtering string-based data, like names or descriptions.
 * - `ApiFilterDateTypes`: Extended date-related filter types, supporting
 *   date-specific filtering needs (definition provided elsewhere in the codebase).
 */
export type ApiFilterType =  "ID"
    | "ID_STRING"
    | "BOOL"
    | "STRING"
    | ApiFilterDateTypes

/**
 * Represents sorting instructions for data retrieval operations.
 *
 * This interface is used to specify the criteria for sorting a collection of data,
 * such as a database query or an API response, by a particular field and direction.
 *
 * @property fieldName - The name of the field by which data should be sorted.
 *                        This should align with the field names in the data source.
 * @property direction - The sorting direction. A value of `true` indicates ascending order (ASC),
 *                       while `false` indicates descending order (DESC).
 *
 * Note: The interpretation of `fieldName` and `direction` may rely on the underlying implementation.
 * Ensure the specified `fieldName` is a valid key in the data structure being sorted.
 */
export interface OrderBy {
    fieldName: string
    direction: boolean // true equals ASC
}

/**
 * Represents the ability to determine if an object is sortable and provides optional metadata for sorting logic.
 *
 * This interface defines the structure for objects that support sorting operations,
 * including whether they can be sorted and, if applicable, the specific field used as the sorting key.
 */
export interface Sortable {
    isSortable: boolean
    sortFieldIdentifier?: string
}

/**
 * Represents an entity that can be toggled between visible or hidden states.
 *
 * This interface is used to define objects that have visibility as a characteristic,
 * allowing developers to build features like show/hide functionality in UI components
 * or conditional rendering logic.
 *
 * @property isVisible A boolean indicating whether the entity is currently visible.
 * When true, the entity is visible; when false, the entity is hidden.
 */
export interface Hideable {
    isVisible: boolean
}

/**
 * Represents an entity that can be positioned within a sequence or structure.
 *
 * This interface is designed to standardize objects that require a specific order or location.
 * It establishes a contract for objects that need a numerical position marker.
 */
export interface Positionable {
    position: number
}

/**
 * Represents a callback function for handling changes to a user's view settings.
 *
 * This is used to update and react to changes when a user modifies their view preferences in the application.
 * It helps ensure user settings are consistently applied and reflected in the UI.
 *
 * @param newSettings - The updated set of user view settings that should be applied. This object contains
 * relevant preferences such as layout, theme, or display configurations.
 */
export type UserViewSettingsChangeFunction = ( newSettings: UserViewSettings  ) => void

/**
 * Represents an action or operation tied to a change in user-configurable settings.
 *
 * This interface defines the contract for components or modules that can respond
 * to or handle changes in application settings. It serves as a foundational building
 * block for propagating and managing updates to settings across the system.
 *
 * @extends ViewIdentifiable Indicates that this interface inherits functionality for identifying views or components within the UI framework.
 */
export interface SettingsChangeCallable extends ViewIdentifiable {

}

/**
 * A utility class for constructing API filters used in querying and managing data.
 *
 * This class provides a fluent API to define and configure filters with specific properties,
 * such as type, identifier, inversion status, and display name, eventually building a fully
 * configured filter with an associated value.
 */
export class ApiFilterBuilder {
    private type: ApiFilterType = DataFilterTypes.ID;
    private identifier: string = "";
    private isInverted: boolean = false;
    private displayName: string = "";

    /**
     * Updates the filter type for the current instance.
     *
     * This method allows setting the type of filter to be used. It modifies
     * the `type` property of the instance and returns the instance to facilitate
     * method chaining. It ensures that the filter type is updated as needed
     * for API requests or other operations.
     *
     * @param type - The new filter type to assign. This must be a valid `ApiFilterType` value.
     * @return The current instance of the class for method chaining.
     */
    setType( type: ApiFilterType ) {
        this.type = type;
        return this;
    }

    /**
     * Sets a custom display name for the current instance.
     *
     * This method allows configuring a human-readable name for the instance, which can be useful
     * for UI representations or logging purposes. The method updates the `displayName` property
     * and returns the updated instance to allow method chaining.
     *
     * @param name The new display name to assign. It is expected to be a non-empty string.
     * @return The current instance with the updated `displayName` property for method chaining.
     * @throws Will not throw explicitly but ensure that `name` is not undefined, null, or an invalid string.
     */
    setDisplayName( name: string ) {
        this.displayName = name;
        return this;
    }

    /**
     * DATA_FILTER_PREFIX will be set automatically
     * @param fieldIdentifier
     */
    setIdentifier( fieldIdentifier: string ) {
        this.identifier = DATA_FILTER_PREFIX + fieldIdentifier;
        return this;
    }

    setIsInverted( isInverted: boolean ) {
        this.isInverted = isInverted;
        return this;
    }

    /**
     * Builds and returns an `ActiveDataFilter` object populated with the provided value.
     * This method is useful for creating filter configurations with pre-set values.
     *
     * The method ensures all required fields are included and combines the provided value
     * with instance-specific properties to create a fully defined filter object.
     *
     * @param value - The value to be assigned as the `selectedValue` in the resulting `ActiveDataFilter`.
     *                This can be of any type that matches the generic type `T`.
     * @return An `ActiveDataFilter` object containing the instance's type, identifier,
     *         inversion status, display name, and the provided value as the `selectedValue`.
     */
    buildWithValue<T>( value: T ): ActiveDataFilter<T> {
        return {
            type: this.type,
            identifier: this.identifier,
            isInverted: this.isInverted,
            displayName: this.displayName,
            selectedValue: value
        };
    }
}